<template>
  <div
    v-if="message || $slots.default"
    :role="variant === 'error' ? 'alert' : 'status'"
    class="flex items-start gap-2.5 border px-3.5 py-2.5 text-sm"
    :class="[variantClasses, square ? 'rounded-none' : 'rounded-lg']"
  >
    <!-- 12px: the glyph's circle (20/24 of the box) matches the cap height of
         the 14px text; offset to center it on the first line (by 5px, plus
         the 5px text padding when there are actions) -->
    <component
      :is="iconComponent"
      class="shrink-0 text-[12px]"
      :class="$slots.actions ? 'mt-2.5' : 'mt-[5px]'"
      aria-hidden="true"
    />
    <div
      v-if="$slots.actions"
      class="min-w-0 flex-1 flex flex-wrap items-start gap-x-4 gap-y-2"
    >
      <!-- padded so its first line, the icon and the h-8 buttons share a center -->
      <div class="min-w-0 flex-1 basis-64 py-[5px] whitespace-pre-line leading-relaxed">
        <slot>{{ message }}</slot>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <slot name="actions" />
      </div>
    </div>
    <div v-else class="min-w-0 whitespace-pre-line leading-relaxed">
      <slot>{{ message }}</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import MdiAlertCircleOutline from "~icons/mdi/alert-circle-outline";
import MdiAlertOutline from "~icons/mdi/alert-outline";
import MdiCheckCircleOutline from "~icons/mdi/check-circle-outline";
import MdiInformationOutline from "~icons/mdi/information-outline";

export type AlertVariant = "error" | "success" | "warning" | "info";

const props = withDefaults(
  defineProps<{
    /** Message text; alternatively pass content through the default slot. */
    message?: string | null;
    variant?: AlertVariant;
    /** Square corners, for a full-width strip flush with the app shell. */
    square?: boolean;
  }>(),
  {
    message: "",
    variant: "info",
    square: false,
  },
);

// One inline message component for form/page feedback, on the status tokens.
// Errors announce assertively (role="alert"); the rest are polite statuses.
// The `actions` slot holds the alert's buttons (size "sm": "primary" for the
// main action, "secondary" for the others), at the end of the text and
// wrapping below it on narrow screens.
const styles: Record<AlertVariant, string> = {
  error:
    "bg-danger-bg dark:bg-danger-bg-dark border-danger-border/60 dark:border-danger-border-dark/60 text-danger-text dark:text-danger-text-dark",
  success:
    "bg-status-safe dark:bg-status-safe-dark border-status-safe-border/70 text-status-safe-icon dark:text-status-safe-border",
  warning:
    "bg-status-analyzed dark:bg-status-analyzed-dark border-status-analyzed-border text-status-analyzed-icon dark:text-status-analyzed-border",
  info: "bg-status-completed dark:bg-status-completed-dark border-status-completed-border/60 text-status-completed-icon dark:text-status-completed-border",
};

const icons: Record<AlertVariant, unknown> = {
  error: MdiAlertCircleOutline,
  success: MdiCheckCircleOutline,
  warning: MdiAlertOutline,
  info: MdiInformationOutline,
};

const variantClasses = computed(() => styles[props.variant]);
const iconComponent = computed(() => icons[props.variant]);
</script>
